import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  useLazyGetFireFighterAccessConfigQuery,
  useRenewTokensMutation,
  useLazyGetFireFighterAuthorizeQuery,
} from '../../api/auth/AuthApi'
import {
  setIsFireFighterAccessEnabled,
  setAtlasData,
  setRefreshToken,
  setIsFirefighterLoading,
} from '../../redux/reducers/AtlasSandReducer'
import {
  UI_CONSTANTS,
  FF_SESSION_KEY,
} from '../../utils/constants/apiConstants'
import { showToast } from '../../components/common/toast/Toast'

// Decode JWT expiry without any library — same approach MSAL uses internally
const isJwtExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    // 30-second buffer so we don't use a token that's about to expire
    return Date.now() >= (payload.exp - 30) * 1000
  } catch {
    return true
  }
}

export const useFirefighterAuth = (isFirefighterPath: boolean) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['translation'])
  const [getFireFighterAccessConfig] = useLazyGetFireFighterAccessConfigQuery()
  const [renewTokens] = useRenewTokensMutation()
  const [getFireFighterAuthorize] = useLazyGetFireFighterAuthorizeQuery()

  const displayErrorToast = (message: string, code: number | string) => {
    showToast(`${message}(${code})`, 'error')
  }

  const restoreSession = () => {
    const stored = localStorage.getItem(FF_SESSION_KEY)
    if (!stored) return

    let session: Record<string, unknown>
    try {
      session = JSON.parse(stored)
    } catch {
      localStorage.removeItem(FF_SESSION_KEY)
      return
    }

    // accessToken still valid — call /FireFighterAuthorize to get fresh user/permissions data.
    if (session.accessToken && !isJwtExpired(session.accessToken as string)) {
      dispatch(setIsFirefighterLoading(true))
      getFireFighterAuthorize(session.accessToken as string)
        .unwrap()
        .then((authorizeData) => {
          // /FireFighterAuthorize returns { code, data: <userdata>, totalCount, requestStatus }
          if (!authorizeData?.requestStatus) {
            displayErrorToast(
              t(`messages.${authorizeData?.code}`),
              authorizeData?.code,
            )
            localStorage.removeItem(FF_SESSION_KEY)
            return
          }
          dispatch(setRefreshToken(session.refreshToken as string))
          dispatch(
            setAtlasData({
              method: UI_CONSTANTS.AUTHORIZED_USER,
              data: authorizeData.data,
            }),
          )
        })
        .catch((error) => {
          displayErrorToast(t(`messages.${error?.status}`), error?.status)
          localStorage.removeItem(FF_SESSION_KEY)
        })
        .finally(() => {
          dispatch(setIsFirefighterLoading(false))
        })
      return
    }

    // accessToken expired — attempt silent refresh via /RenewTokens.
    if (!session.refreshToken) {
      localStorage.removeItem(FF_SESSION_KEY)
      return
    }

    // Signal that a refresh is in-flight so the login form is not shown during the async gap.
    dispatch(setIsFirefighterLoading(true))
    renewTokens(session.refreshToken as string)
      .unwrap()
      .then((tokens) => {
        // /RenewTokens returns { code, data: { accessToken, refreshToken }, totalCount, requestStatus }
        if (!tokens?.requestStatus) {
          displayErrorToast(t(`messages.${tokens?.code}`), tokens?.code)
          localStorage.removeItem(FF_SESSION_KEY)
          return
        }
        const { accessToken, refreshToken } = tokens.data
        // Call /FireFighterAuthorize to get full user/permissions data with the new token
        return getFireFighterAuthorize(accessToken)
          .unwrap()
          .then((authorizeData) => {
            // /FireFighterAuthorize returns { code, data: <userdata>, totalCount, requestStatus }
            if (!authorizeData?.requestStatus) {
              displayErrorToast(
                t(`messages.${authorizeData?.code}`),
                authorizeData?.code,
              )
              localStorage.removeItem(FF_SESSION_KEY)
              return
            }
            localStorage.setItem(
              FF_SESSION_KEY,
              JSON.stringify({ accessToken, refreshToken }),
            )
            dispatch(setRefreshToken(refreshToken))
            dispatch(
              setAtlasData({
                method: UI_CONSTANTS.AUTHORIZED_USER,
                data: authorizeData.data,
              }),
            )
          })
      })
      .catch((error) => {
        // Refresh failed — token truly expired, force re-login
        displayErrorToast(t(`messages.${error?.status}`), error?.status)
        localStorage.removeItem(FF_SESSION_KEY)
      })
      .finally(() => {
        dispatch(setIsFirefighterLoading(false))
      })
  }

  useEffect(() => {
    const hasStoredSession = !!localStorage.getItem(FF_SESSION_KEY)

    if (isFirefighterPath) {
      dispatch(setIsFireFighterAccessEnabled(null))
      getFireFighterAccessConfig(undefined)
        .unwrap()
        .then(({ isFireFighterAccessEnabled }) => {
          dispatch(
            setIsFireFighterAccessEnabled(isFireFighterAccessEnabled ?? false),
          )
          if (isFireFighterAccessEnabled) {
            restoreSession()
          } else {
            localStorage.removeItem(FF_SESSION_KEY)
          }
        })
        .catch((error: { status: number }) => {
          displayErrorToast(t(`messages.${error?.status}`), error?.status)
          localStorage.removeItem(FF_SESSION_KEY)
          dispatch(setIsFireFighterAccessEnabled(false))
        })
    } else if (hasStoredSession) {
      dispatch(setIsFireFighterAccessEnabled(true))
      restoreSession()
    } else {
      dispatch(setIsFireFighterAccessEnabled(false))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}
